/* eslint-disable import/exports-last */
import CodonationCalculator from '@/donationForm/_dependencies/CodonationCalculator'
import analytics, { getDonationFormType } from '@betterplace/product-analytics'
import useDonationFormValues from '../useDonationFormValues'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import { useDonationFormConfig } from '../../DonationFormConfigContext'
import type { CodonationChangeData } from '@betterplace/product-analytics'
export const ActionTypes = {
  Change: 'change',
  Reset: 'reset',
  AmountChanged: 'amount_changed',
} as const

export function ResetAction() {
  return { type: ActionTypes.Reset }
}
export function AmountChangedAction(value: number) {
  return { type: ActionTypes.AmountChanged, value }
}
export function ChangeAction(value: number) {
  return { type: ActionTypes.Change, value }
}

export type Action =
  | ReturnType<typeof ResetAction>
  | ReturnType<typeof AmountChangedAction>
  | ReturnType<typeof ChangeAction>

export type CodonationChangeOwnData = Pick<
  CodonationChangeData,
  | 'donation_amount'
  | 'codonation_amount'
  | 'bucket_index'
  | 'reward_index'
  | 'no_of_increases'
  | 'no_of_decreases'
  | 'percent_of_bucket_bracket'
  | 'percent_of_reward_bracket'
  | 'percent_of_max'
  | 'max'
  | 'step'
>

export function getPercentageFromIndex(values: readonly number[], index: number | null, value: number, max: number) {
  let i = index ?? -1
  const start = index !== null ? values[index] : 0
  let nextValue: number | undefined = value
  if (start === undefined) return null
  do {
    i++
    nextValue = values[i]
    if (nextValue === undefined) {
      nextValue = max
      break
    }
  } while (nextValue <= start)
  if (!nextValue) return null
  const percentage = Math.round(((value - start) / (nextValue - start)) * 100)
  return percentage
}

export function calculateCodonationChangeData(state: CodonationChangeOwnData): CodonationChangeOwnData {
  const { findBucketIndexByValue, findRewardIndexByValue } = CodonationCalculator.getCodonationHelpers(
    state.donation_amount
  )
  const bucketIndex = findBucketIndexByValue(state.codonation_amount)
  const rewardIndex = findRewardIndexByValue(state.codonation_amount)
  const config = CodonationCalculator.findConfig(state.donation_amount)
  const percentOfTheBucket = getPercentageFromIndex(
    config.minBucketValue,
    bucketIndex,
    state.codonation_amount,
    state.max
  )
  const percentOfTheReward = getPercentageFromIndex(
    config.minRewardValue,
    rewardIndex,
    state.codonation_amount,
    state.max
  )

  return {
    ...state,
    bucket_index: bucketIndex,
    reward_index: rewardIndex,
    percent_of_bucket_bracket: percentOfTheBucket,
    percent_of_reward_bracket: percentOfTheReward,
    percent_of_max: Math.round((state.codonation_amount / state.max) * 100),
  }
}

function getInitialState(coDonationAmount: number, amountInCents: number): CodonationChangeOwnData {
  const max = CodonationCalculator.max(amountInCents)
  const step = CodonationCalculator.step(amountInCents)
  return {
    donation_amount: amountInCents,
    codonation_amount: coDonationAmount,
    no_of_increases: 0,
    no_of_decreases: 0,
    reward_index: null,
    bucket_index: null,
    percent_of_reward_bracket: null,
    percent_of_bucket_bracket: null,
    percent_of_max: Math.round((coDonationAmount / max) * 100),
    max,
    step,
  }
}

export function reducer(prev: CodonationChangeOwnData, action: Action): CodonationChangeOwnData {
  switch (action.type) {
    case ActionTypes.Change: {
      if (prev.codonation_amount === action.value) return prev
      const no_of_increases = prev.codonation_amount < action.value ? prev.no_of_increases + 1 : prev.no_of_increases
      const no_of_decreases = prev.codonation_amount > action.value ? prev.no_of_decreases + 1 : prev.no_of_decreases
      return calculateCodonationChangeData({
        ...prev,
        codonation_amount: action.value,
        no_of_increases,
        no_of_decreases,
      })
    }
    case ActionTypes.Reset:
      return getInitialState(prev.codonation_amount, prev.donation_amount)
    case ActionTypes.AmountChanged:
      return calculateCodonationChangeData({
        ...prev,
        max: CodonationCalculator.max(action.value),
        step: CodonationCalculator.step(action.value),
        no_of_increases: 0,
        no_of_decreases: 0,
        donation_amount: action.value,
      })
    default:
      return prev
  }
}

function reducerWithLogging(state: CodonationChangeOwnData, action: Action): CodonationChangeOwnData {
  const next = reducer(state, action)
  return next
}

function useCodonationAnalytics(initialCodonationAmout: number) {
  const initialCodonationRef = useRef(initialCodonationAmout)
  const [amountInCents] = useDonationFormValues(['amount_cents'])
  const [data, dispatch] = useReducer(reducerWithLogging, getInitialState(initialCodonationRef.current, amountInCents))
  const ref = useRef(data)
  ref.current = data

  useEffect(() => {
    dispatch(AmountChangedAction(amountInCents))
  }, [amountInCents])

  const { receiverId, receiverType, channel, showCodonation } = useDonationFormConfig()

  const commitHandler = useCallback(() => {
    analytics.coDonationChange({
      ...ref.current,
      id: receiverId,
      receiver_type: receiverType,
      donation_form_type: getDonationFormType({
        receiverId,
        receiverType,
        channel,
        showCodonation,
      }),
    })
    dispatch(ResetAction())
  }, [channel, receiverId, receiverType, showCodonation])

  const reset = useCallback(() => {
    dispatch(ResetAction())
  }, [])

  const changeValue = useCallback((value: number) => {
    dispatch(ChangeAction(value))
  }, [])

  return { commitHandler, reset, changeValue }
}

export default useCodonationAnalytics
